import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X, ChevronRight, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useTheme } from '../contexts/ThemeContext';
import DarkModeToggle from '../components/DarkModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CourseContentEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const token = localStorage.getItem('session_token');
  
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [viewMode, setViewMode] = useState('expanded'); // 'expanded' or 'collapsed'
  
  // Fetch course and content
  useEffect(() => {
    fetchCourseData();
  }, [courseId]);
  
  const fetchCourseData = async () => {
    try {
      setLoading(true);
      
      // Fetch course details using admin endpoint (no enrollment check)
      const courseRes = await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const courseData = await courseRes.json();
      setCourse(courseData);
      
      // Fetch all lessons (flat structure - no sections/modules)
      const lessonsRes = await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}/sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const lessonsData = await lessonsRes.json();
      
      // Filter to text and url type lessons (not PDFs or other source types)
      const lessons = (lessonsData || [])
        .filter(source => source.source_type === 'text' || source.source_type === 'url')
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setSections(lessons); // Reusing state name for simplicity
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching course data:', error);
      setLoading(false);
    }
  };
  
  // Add new lesson
  const addLesson = async () => {
    const formData = new FormData();
    formData.append('title', 'New Lesson');
    formData.append('content', '## Lesson Content\n\nAdd your lesson content here...');
    formData.append('source_type', 'text');
    formData.append('order', sections.length);
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}/sources`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      fetchCourseData();
    } catch (error) {
      console.error('Error adding lesson:', error);
    }
  };
  
  // Add new external link/URL resource
  const addExternalLink = async () => {
    const url = window.prompt('Enter the URL for the external resource (e.g., NotebookLM link):');
    if (!url) return;
    
    const title = window.prompt('Enter a title for this resource:', 'External Resource');
    if (!title) return;
    
    const formData = new FormData();
    formData.append('title', title);
    formData.append('url', url);
    formData.append('source_type', 'url');
    formData.append('order', sections.length);
    formData.append('content', `This is an interactive external resource. Click the button above to open it.`);
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}/sources`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      fetchCourseData();
    } catch (error) {
      console.error('Error adding external link:', error);
    }
  };
  
  // Functions removed - using flat lesson structure now
  
  // Update lesson
  const updateLesson = async (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sources/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        fetchCourseData();
        setEditingItem(null);
      } else {
        const error = await response.json();
        alert(`Error saving: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating lesson:', error);
      alert('Error saving changes. Please try again.');
    }
  };
  
  // Delete lesson
  const deleteLesson = async (id) => {
    if (!window.confirm('⚠️ Delete this lesson?\n\nThis action CANNOT be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}/sources/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('✅ Lesson deleted successfully!');
        fetchCourseData();
      } else {
        const error = await response.json();
        alert(`❌ Error: ${error.detail || 'Failed to delete'}`);
      }
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('❌ Error deleting lesson. Please try again.');
    }
  };
  
  // Edit dialog state (moved outside to prevent re-render issues)
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    content: '',
    url: '',
    block_color: '#3B82F6',
    thumbnail_url: ''
  });
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  
  // Update form data when editingItem changes
  useEffect(() => {
    if (editingItem) {
      setEditFormData({
        title: editingItem.data.title || '',
        description: editingItem.data.description || '',
        content: editingItem.data.content || '',
        url: editingItem.data.url || '',
        block_color: editingItem.data.block_color || '#3B82F6',
        thumbnail_url: editingItem.data.thumbnail_url || ''
      });
    }
  }, [editingItem]);
  
  const presetColors = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Green', value: '#22C55E' },
    { name: 'Teal', value: '#14B8A6' },
    { name: 'Navy', value: '#102862' },
  ];
  
  const handleThumbnailUpload = async (file) => {
    if (!file || !editingItem) return;
    setUploadingThumbnail(true);
    
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}/sources/${editingItem.data.id}/thumbnail`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData
      });
      
      if (response.ok) {
        const data = await response.json();
        setEditFormData(prev => ({ ...prev, thumbnail_url: data.thumbnail_url }));
      } else {
        alert('Failed to upload thumbnail');
      }
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      alert('Error uploading thumbnail');
    }
    setUploadingThumbnail(false);
  };
  
  const handleRemoveThumbnail = async () => {
    if (!editingItem) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}/sources/${editingItem.data.id}/thumbnail`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditFormData(prev => ({ ...prev, thumbnail_url: '' }));
    } catch (error) {
      console.error('Error removing thumbnail:', error);
    }
  };
  
  const handleEditSave = async () => {
    if (!editingItem) return;
    const isUrlType = editingItem.data.source_type === 'url';
    
    const dataToSend = {
      title: editFormData.title,
      content: editFormData.content
    };
    if (isUrlType) {
      dataToSend.url = editFormData.url;
      dataToSend.block_color = editFormData.block_color;
    }
    await updateLesson(editingItem.data.id, dataToSend);
  };
  
  // Edit dialog render
  const renderEditDialog = () => {
    if (!editingItem) return null;
    const isUrlType = editingItem.data.source_type === 'url';
    
    return (
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto light-form-card">
          <DialogHeader>
            <DialogTitle>{isUrlType ? 'Edit External Link' : 'Edit Lesson'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{isUrlType ? 'Resource Title' : 'Lesson Title'}</label>
              <Input
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                placeholder={isUrlType ? "Enter resource title..." : "Enter lesson title..."}
              />
            </div>
            
            {isUrlType && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">External URL</label>
                  <Input
                    type="url"
                    value={editFormData.url}
                    onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
                    placeholder="https://notebooklm.google.com/notebook/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the full URL. Students will click a button to open this in a popup window.
                  </p>
                </div>
                
                {/* Thumbnail Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Thumbnail Image (Optional)</label>
                  <div className="flex items-start gap-4">
                    {editFormData.thumbnail_url ? (
                      <div className="relative">
                        <img 
                          src={editFormData.thumbnail_url.startsWith('http') ? editFormData.thumbnail_url : `${BACKEND_URL}${editFormData.thumbnail_url}`}
                          alt="Thumbnail preview"
                          className="w-20 h-20 rounded-lg object-cover border"
                        />
                        <button
                          onClick={handleRemoveThumbnail}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs text-center">
                        No image
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleThumbnailUpload(file);
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        disabled={uploadingThumbnail}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {uploadingThumbnail ? 'Uploading...' : 'Upload a small icon or logo for this resource'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Color Picker */}
                <div>
                  <label className="block text-sm font-medium mb-2">Block Color</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {presetColors.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setEditFormData({ ...editFormData, block_color: color.value })}
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          editFormData.block_color === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={editFormData.block_color}
                      onChange={(e) => setEditFormData({ ...editFormData, block_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={editFormData.block_color}
                      onChange={(e) => setEditFormData({ ...editFormData, block_color: e.target.value })}
                      placeholder="#3B82F6"
                      className="w-32"
                    />
                    <span className="text-xs text-gray-500">Custom color</span>
                  </div>
                  {/* Preview */}
                  <div className="mt-3 p-3 rounded-lg border-2" style={{
                    background: `linear-gradient(to right, ${editFormData.block_color}10, ${editFormData.block_color}20)`,
                    borderColor: `${editFormData.block_color}40`
                  }}>
                    <p className="text-xs text-gray-500">Preview of block appearance</p>
                  </div>
                </div>
              </>
            )}
            
            {!isUrlType && (
              <div>
                <label className="block text-sm font-medium mb-2">Lesson Content (Markdown supported)</label>
                <textarea
                  value={editFormData.content}
                  onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
                  rows={20}
                  className="w-full border rounded-md p-3 font-mono text-sm"
                  placeholder="Enter lesson content with Markdown formatting..."
                />
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setEditingItem(null)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button type="button" onClick={handleEditSave} style={{ background: '#102862', color: 'white' }}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ background: isDarkMode ? '#092036' : '#F9FAFB' }}>
        <div className="max-w-6xl mx-auto">
          <p style={{ color: isDarkMode ? '#ffffff' : undefined }}>Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen p-8" style={{ background: isDarkMode ? '#092036' : '#F9FAFB' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              style={{ color: isDarkMode ? '#e2e8f0' : undefined }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <DarkModeToggle />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>
                Course Content Editor
              </h1>
              <p className="text-lg" style={{ color: isDarkMode ? '#e2e8f0' : '#70767C' }}>
                {course?.title}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={addExternalLink} variant="outline" style={{ borderColor: '#102862', color: isDarkMode ? '#60a5fa' : '#102862' }}>
                <Plus className="w-4 h-4 mr-2" />
                Add External Link
              </Button>
              <Button onClick={addLesson} style={{ background: '#102862', color: 'white' }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Lesson
              </Button>
            </div>
          </div>
        </div>
        
        {/* Help Guide */}
        <Card className="p-4 mb-6" style={{ backgroundColor: isDarkMode ? '#0f2744' : '#eff6ff', borderColor: isDarkMode ? '#1e3a5f' : '#bfdbfe' }}>
          <div className="flex items-start gap-3">
            <div className="text-sm" style={{ color: isDarkMode ? '#60a5fa' : '#2563eb' }}>ℹ️</div>
            <div className="flex-1">
              <h3 className="font-semibold mb-2" style={{ color: isDarkMode ? '#ffffff' : '#1e3a8a' }}>Course Content</h3>
              <div className="text-sm space-y-1" style={{ color: isDarkMode ? '#e2e8f0' : '#1e40af' }}>
                <p>✏️ <strong>Edit:</strong> Click &quot;Edit&quot; to modify lesson titles and content (Markdown supported)</p>
                <p>➕ <strong>Add Lesson:</strong> Creates navigable lesson content students progress through</p>
                <p>🔗 <strong>Add External Link:</strong> Creates a study resource (e.g., NotebookLM) shown as a persistent button below the instructor bio - students can access anytime</p>
                <p>🗑️ <strong>Delete:</strong> Click &quot;Delete&quot; to remove items permanently</p>
                <p className="text-xs mt-2" style={{ color: isDarkMode ? '#93c5fd' : '#1d4ed8' }}>💡 Tip: External links appear as &quot;Interactive Study&quot; in the sidebar, not in the lesson list</p>
              </div>
            </div>
          </div>
        </Card>
        
        {/* Lessons List */}
        <Card className="p-6 light-form-card" style={{ backgroundColor: isDarkMode ? '#0f2744' : 'white' }}>
          {sections.length === 0 ? (
            <div className="text-center py-12">
              <p className="mb-4" style={{ color: isDarkMode ? '#e2e8f0' : '#6b7280' }}>No lessons yet. Click &quot;Add Lesson&quot; to start creating course content.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map((lesson, idx) => (
                <div key={lesson.id} className="border rounded-lg p-6 bg-white hover:shadow-md transition-shadow" style={{ borderColor: '#DADEEO' }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-semibold px-2 py-1 rounded" style={{ 
                          background: lesson.source_type === 'url' ? '#FEF3C7' : '#E0E7FF', 
                          color: lesson.source_type === 'url' ? '#92400E' : '#102862' 
                        }}>
                          {lesson.source_type === 'url' ? '🔗 External Link' : `Lesson ${idx + 1}`}
                        </span>
                        <h3 className="text-lg font-bold" style={{ color: '#092036' }}>
                          {lesson.title}
                        </h3>
                      </div>
                      {lesson.source_type === 'url' && lesson.url && (
                        <div className="text-sm text-blue-600 mt-2 flex items-center gap-2">
                          <span className="truncate max-w-md">{lesson.url}</span>
                          <a 
                            href={lesson.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs bg-blue-100 px-2 py-1 rounded hover:bg-blue-200"
                          >
                            Test Link ↗
                          </a>
                        </div>
                      )}
                      {lesson.content && lesson.source_type !== 'url' && (
                        <div className="text-sm text-gray-700 mt-3 p-4 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
                          <p className="whitespace-pre-wrap">
                            {lesson.content.substring(0, 500)}
                            {lesson.content.length > 500 && '...'}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingItem({ type: 'lesson', data: lesson });
                        }}
                        className="whitespace-nowrap"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteLesson(lesson.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 whitespace-nowrap"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      
      {renderEditDialog()}
    </div>
  );
}
